import { useMemo } from 'react';
import { IOrganisationMarketingData } from 'shared';

import { Select } from '../../../../common/Atoms/Select';
import { TextArea } from '../../../../common/Atoms/TextArea';
import { CheckBox } from '../../../../common/Atoms/CheckBox';
import { PhoneNumberInput } from '../../../../common/Atoms/PhoneNumberInput';
import { PRIVACY_POLICY, TERMS_AND_CONDITIONS } from '../../../constants/links';

const heardAboutOptions = [
  {
    label: `App Marketplace. (Xero, QuickBooks, etc.)`,
    value: `appstore`,
  },
  {
    label: `Search Engine (Google, Bing, etc.)`,
    value: `search`,
  },
  {
    label: `My accountant or bookkeeper`,
    value: `partner`,
  },
  {
    label: `Blog or article`,
    value: `blog`,
  },
  {
    label: `AI Tools - ChatGPT, Claude, Gemini etc`,
    value: `aitools`,
  },
  {
    label: `Tradeshow or event`,
    value: `event`,
  },
  {
    label: `Newsletter`,
    value: `newsletter`,
  },
  {
    label: `Social Media`,
    value: `social`,
  },
  {
    label: `Pinch Payments`,
    value: `pinch`,
  },
  {
    label: `Other`,
    value: `other`,
  },
];

/** Randomised to avoid order bias in responses; "Other" stays last */
function shuffledHeardAboutOptions() {
  const options = heardAboutOptions.filter(o => o.value !== `other`);

  for (let i = options.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [options[i], options[j]] = [options[j], options[i]];
  }

  return [...options, ...heardAboutOptions.filter(o => o.value === `other`)];
}

const imAOptions = [
  {
    label: `Business`,
    description: `I'm a business sending invoices to my customers`,
    value: `business`,
  },
  {
    label: `Accountant or Bookkeeper for Clients`,
    description: `I'm an accountant or bookkeeper setting up on behalf of my clients`,
    value: `partner`,
  },
  {
    label: `Accountant or Bookkeeper for Myself`,
    description: `I'm an accountant or bookkeeper setting up for my own practice`,
    value: `partner_self`,
  },
  {
    label: `Something else`,
    description: `None of the above apply`,
    value: `other`,
  },
];

interface IWelcomeQuestionsProps {
  welcomeQuestions: IOrganisationMarketingData;
  setWelcomeQuestions: (value: IOrganisationMarketingData) => void;
}

export function WelcomeQuestions({ welcomeQuestions, setWelcomeQuestions }: IWelcomeQuestionsProps) {
  const heardAbout = useMemo(shuffledHeardAboutOptions, []);

  return (
    <div className={ `max-w-[640px] space-y-7` }>
      <TextArea
        rows={ 4 }
        label={ `In your own words, what are you looking to achieve with Paidnice?` }
        subHeading={ `This will help us understand your needs better when contacting support or getting help setting up.` }
        placeholder={ `e.g I want Paidnice to automatically calculate and issue interest on overdue invoices.` }
        value={ welcomeQuestions.lookingToAchieve }
        onChange={ value => setWelcomeQuestions({ ...welcomeQuestions, lookingToAchieve: value?.value?.toString() }) }
      />

      <div className={ `grid gap-5 md:grid-cols-2` }>
        <Select
          label={ `What best describes who you are? ` }
          options={ imAOptions }
          selected={ welcomeQuestions.typeSelfReported }
          onChange={ value => setWelcomeQuestions({ ...welcomeQuestions, typeSelfReported: value.value }) }
          nullable
        />

        <Select
          label={ `How did you hear about us?` }
          options={ heardAbout }
          selected={ welcomeQuestions.sourceSelfReported }
          onChange={ value => setWelcomeQuestions({ ...welcomeQuestions, sourceSelfReported: value.value }) }
          nullable
        />
      </div>

      <PhoneNumberInput
        label={ `Contact Phone Number` }
        name={ `contactNumber` }
        value={ welcomeQuestions.contactNumber }
        subHeading={ `A contact phone number we can reach you on.` }
        placeholder={ `eg. 555 123 4567` }
        required
        onChange={ value => setWelcomeQuestions({ ...welcomeQuestions, contactNumber: value.value }) }
      />

      <CheckBox
        label={ (
          <span>
            { `By using Paidnice I agree to the ` }
            <a
              href={ TERMS_AND_CONDITIONS }
              target={ `_blank` }
              rel={ `noopener noreferrer` }
              className={ `text-paidniceGreen-600 hover:text-paidniceGreen-700 underline` }
            >
              { `Terms of Service` }
            </a>
            { ` and ` }
            <a
              href={ PRIVACY_POLICY }
              target={ `_blank` }
              rel={ `noopener noreferrer` }
              className={ `text-paidniceGreen-600 hover:text-paidniceGreen-700 underline` }
            >
              { `Privacy Policy` }
            </a>
            <span className={ `text-red-500` }>{ ` *` }</span>
          </span>
        ) as unknown as string }
        checked={ welcomeQuestions.agreedToTerms }
        onChange={ value => setWelcomeQuestions({ ...welcomeQuestions, agreedToTerms: value.value }) }
        placement={ `left` }
      />
    </div>
  );
}
